import { useEffect, useRef, useState, type ReactElement } from "react";
import { api } from "../../api/client";
import { NumPadPop } from "../../components/shared";
import { AvatarImg, VideoThumb } from "../dc/dcVideoStatsParts";
import { MediaViewerOo, type MaterialUrls } from "./MediaViewerOo";
import type { OoFan, OoMaterial, OoOperatorRow } from "../../hooks/useOoReports";
import css from "./dashboard.module.css";

const fmtR = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
/** МирКв-отметка символами формы оператора: score → 5.0 (золото), cancel → ✕ (красный). */
const Mk = ({ s }: { s: string }): ReactElement | null =>
  s === "score" ? <span className={css.opMkScore}>5.0</span> : s === "cancel" ? <span className={css.opMkCancel}>✕</span> : null;

/** Чип оператора в карточке отчёта (23.08, Саша): кликабельный — попап деталей;
 *  жёлтая цифра такси; его МирКв-отметка; ставка — из назначения или таблицы.
 *  Тап по цифре ставки (24.08, владелец) — стандартный NumPadPop с клавишами
 *  «Записать — эта игра» (assignments.rate_paid) и «Перезаписать в таблицу
 *  ставок» (PUT rates — прошлые игры бэк штампует старой — + rate_paid игры);
 *  в лейбле — справка о стандартной ставке из сетки. */
export function OpChip({ row, questId, isMirKv, onSaved, onTaxi }: { row: OoOperatorRow; questId: string; isMirKv: boolean; onSaved: () => void; onTaxi?: (v: number) => Promise<void> | void }): ReactElement {
  const [open, setOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [taxiOpen, setTaxiOpen] = useState(false);
  const [rateErr, setRateErr] = useState("");
  /* 28.08 (владелец): правка ЗП/такси обновляет ТОЛЬКО этот чип, без reload страницы */
  const [localRate, setLocalRate] = useState<number | null>(null);
  const [localTaxi, setLocalTaxi] = useState<number | null>(null);
  const rate = localRate ?? row.ratePaid;
  const taxi = localTaxi ?? row.taxi;
  const rateRef = useRef<HTMLSpanElement | null>(null);
  const taxiRef = useRef<HTMLSpanElement | null>(null);
  const canTaxi = !!onTaxi && row.reportId != null;
  useEffect(() => {
    if (!open && !rateErr) return;
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.opPopWrap}`)) { setOpen(false); setRateErr(""); } };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open, rateErr]);
  const saveRate = async (v: number, toGrid: boolean) => {
    /* 06.09 (владелец): у отменённой игры ставку можно и обнулить — 0 пишется в назначение; в таблицу ставок 0 не пишем */
    if (!(v >= 0) || (toGrid && !(v > 0))) return;
    try {
      if (toGrid) await api("/api/rates/operators", { method: "PUT", body: { operator_id: row.operatorId, rates: { [questId]: v } } });
      if (row.assignmentId != null) await api(`/api/assignments/${row.assignmentId}`, { method: "PUT", body: { rate_paid: v } });
      setLocalRate(v); /* без reload — обновляем только чип */
    } catch (e) { setRateErr(e instanceof Error ? e.message : "Ошибка сохранения"); }
  };
  return (
    <span className={css.opPopWrap}>
      <button
        type="button" className={`${css.opchip} ${row.hasReport ? css.chipOk : css.chipWarn} ${row.isPrimary ? css.chipPrim : ""} ${row.draftSaved ? css.chipDraft : ""}`}
        title={row.draftSaved ? "Черновик сохранён, отчёт не отправлен" : undefined}
        onClick={(e) => {
          e.stopPropagation();
          const t = e.target as HTMLElement;
          if (t.closest?.(`.${css.opRate}`)) { setRateOpen((o) => !o); setTaxiOpen(false); setOpen(false); }
          else if (t.closest?.(`.${css.opTaxi}`) && canTaxi) { setTaxiOpen((o) => !o); setRateOpen(false); setOpen(false); }
          else { setOpen((o) => !o); setRateOpen(false); setTaxiOpen(false); }
        }}
      >
        {/* 28.08 (владелец): ✓/✗ убраны — сдачу кодирует рамка чипа */}
        <span className={css.opName}>{row.name}</span>
        {isMirKv ? <Mk s={row.mkStatus} /> : null}
        {taxi > 0 ? <span ref={taxiRef} className={css.opTaxi} title={canTaxi ? "Тап — изменить/убрать такси" : undefined}>🚕₽{fmtR(taxi)}</span> : null}
        <span ref={rateRef} className={css.opRate} title="Тап — изменить ставку">₽{fmtR(rate)}</span>
      </button>
      <NumPadPop
        open={rateOpen} anchor={rateRef.current} label={`Ставка · сетка ₽${fmtR(row.rateStd)}`}
        mode="money" value={rate} max={99999}
        /* 02.09 (владелец): галочка = «Записать — эта игра» (без назначения — в таблицу) */
        onCommit={(v) => void saveRate(v, row.assignmentId == null)} onClose={() => setRateOpen(false)}
        extraKeys={[
          { label: "Записать — эта игра", disabled: row.assignmentId == null, onPress: (v) => void saveRate(v, false) },
          { label: "Перезаписать в таблицу", onPress: (v) => void saveRate(v, true) },
        ]}
      />
      <NumPadPop
        open={taxiOpen} anchor={taxiRef.current ?? rateRef.current} label={`Такси · ${row.name}`}
        mode="money" value={taxi} max={99999}
        onCommit={(v) => { void Promise.resolve(onTaxi?.(v)).then(() => setLocalTaxi(v)).catch(() => {}); }} onClose={() => setTaxiOpen(false)}
        extraKeys={[
          { label: "Сохранить", onPress: (v) => { setTaxiOpen(false); void Promise.resolve(onTaxi?.(v)).then(() => setLocalTaxi(v)).catch(() => {}); } },
          { label: "Убрать такси", onPress: () => { setTaxiOpen(false); void Promise.resolve(onTaxi?.(0)).then(() => setLocalTaxi(0)).catch(() => {}); } },
        ]}
      />
      {rateErr ? <div className={css.morePop}><div className={css.rateErr}>{rateErr}</div></div> : null}
      {open ? (
        <div className={css.opPop} onClick={(e) => e.stopPropagation()}>
          <div className={css.opPopNm}>{row.name}{row.nickname ? <span className={css.opPopNick}> · {row.nickname}</span> : null}</div>
          <div className={css.opPopRow}>Отчёт: {row.hasReport ? <b className={css.ok}>сдан</b> : row.draftSaved ? <b className={css.warn}>черновик сохранён, не отправлен</b> : <b className={css.bad}>нет</b>}</div>
          <div className={css.opPopRow}>Ставка за игру: <b>₽{fmtR(rate)}</b> {row.isPrimary ? "· основной" : ""}</div>
          {/* 28.08 (владелец): ставку ОО меняет ВСЕГДА — и из попапа тоже */}
          <button type="button" className={css.opPopBtn} onClick={() => { setOpen(false); setRateOpen(true); }}>₽ изменить ставку…</button>
          {taxi > 0 ? <div className={css.opPopRow}>Такси: <b className={css.gold}>₽{fmtR(taxi)}</b></div>
            : canTaxi ? <button type="button" className={css.opPopBtn} onClick={() => { setOpen(false); setTaxiOpen(true); }}>+ добавить такси</button> : null}
          {isMirKv ? <div className={css.opPopRow}>Мир Квестов: {row.mkStatus === "score" ? <b className={css.gold}>5.0 — оценки</b> : row.mkStatus === "cancel" ? <b className={css.bad}>✕ отмена</b> : "не указал"}</div> : null}
          {row.activity ? <div className={css.opPopRow}>Роль: {row.activity}</div> : null}
          {row.comment ? <div className={css.opPopCmt}>{row.comment}</div> : null}
        </div>
      ) : null}
    </span>
  );
}

/* 07.09 (владелец): рамка чипа = как человек связан с игрой. Голубая — добавился сам
   во время игры по QR оператора; золотая — капитан (заказчик брони); розовая —
   персональная ссылка оператора; зелёная — добавил капитан или кто-то другой.
   Расшифровка рамок — в объяснителе «?» строки сводки (DashHelp), не в карточке. */
const FAN_CLS: Record<OoFan["via"], string> = { qr: "", owner: css.fanOwner ?? "", link: css.fanLink ?? "", invited: css.fanInvited ?? "" };
function fanTitle(f: OoFan): string {
  const who = f.username ? `@${f.username}` : f.name;
  const how = f.via === "owner" ? "капитан: заказчик брони (телефон или карточка клиента)"
    : f.via === "link" ? `вступил по персональной ссылке${f.by ? ` оператора ${f.by}` : " оператора"} с этой игры`
    : f.via === "invited" ? `добавил ${f.by === "сотрудник" ? "сотрудник" : f.by ? `капитан/игрок ${f.by}` : "капитан или другой игрок"}`
    : `добавился сам по QR оператора во время игры${f.by ? `; вступал по ссылке ${f.by}` : ""}`;
  return `${who} — ${how}`;
}

/** Экстры карточки ОО-дашборда: доп-материалы игры с превью (тап — внутренний
 *  просмотрщик) и Unifans-игроки с аватарками (лениво через useAvatarSrc).
 *  10.10: снятое с игры и правленое в просмотрщике обновляет строку медиа сразу, без reload. */
export function DashExtras({ bookingId, materials, unifans, uploader }: { bookingId: number; materials: OoMaterial[]; unifans: OoFan[]; uploader?: ReactElement }): ReactElement | null {
  const [viewId, setViewId] = useState<number | null>(null);
  const [removed, setRemoved] = useState<ReadonlySet<number>>(() => new Set());
  const [patched, setPatched] = useState<Readonly<Record<number, MaterialUrls>>>({});
  const list = materials.filter((m) => !removed.has(m.id)).map((m) => (patched[m.id] ? { ...m, ...patched[m.id] } : m));
  const view = viewId == null ? null : list.find((m) => m.id === viewId) ?? null;
  if (!list.length && !unifans.length && !uploader) return null;
  return (
    <>
      {list.length || uploader ? (
        <div className={css.rcMedia}>
          <span className={css.lbl}>Медиа {list.length}</span>
          {list.map((m) => (
            <button
              key={m.id} type="button" className={css.mediaTh}
              title={m.kind === "video" ? `видео${m.duration ? ` · ${m.duration}` : ""}` : m.kind}
              onClick={(e) => { e.stopPropagation(); if (m.stream || m.poster) setViewId(m.id); }}
            >
              {m.kind === "video" ? (
                <VideoThumb url={m.poster ?? ""} w={46} h={30} />
              ) : m.kind === "photo" ? (
                <img className={css.mediaImg} src={m.poster ?? m.stream ?? ""} alt="" loading="lazy" />
              ) : (
                <span className={css.mediaAu}>♫</span>
              )}
              {m.duration ? <span className={css.mediaDur}>{m.duration}</span> : null}
            </button>
          ))}
          {/* 28.08 (владелец): «+» живёт В блоке медиа и вдвое меньше */}
          {uploader}
        </div>
      ) : null}
      {unifans.length ? (
        <div className={css.rcFans}>
          <span className={css.lbl}>Unifans {unifans.length}</span>
          {unifans.map((f) => (
            <span key={f.telegramId} className={`${css.fanChip} ${FAN_CLS[f.via] ?? ""}`} title={fanTitle(f)}>
              <AvatarImg url={f.avatar} name={f.name} size={18} />
              <span className={css.fanNm}>{f.name}</span>
              {f.verified ? <span className={css.fanOk}>✓</span> : null}
            </span>
          ))}
        </div>
      ) : null}
      {view ? (
        <MediaViewerOo
          key={view.id} m={view} bookingId={bookingId} onClose={() => setViewId(null)}
          onRemoved={(id) => { setRemoved((r) => new Set(r).add(id)); setViewId(null); }}
          onReplaced={(id, urls) => setPatched((p) => ({ ...p, [id]: urls }))}
        />
      ) : null}
    </>
  );
}
